/**
 * Detects when a new build has been deployed while the app is already open
 * in a tab, and reloads automatically - so a deploy just takes effect on
 * its own, no manual (hard) refresh required.
 *
 * How: the production build's index.html references its JS entry point as
 * `/static/js/main.<contenthash>.js` - a filename that only changes when a
 * new build is deployed. Poll `/` (nginx serves it as `Cache-Control:
 * no-store`, so this always reaches the real current file, never a cached
 * one) every INTERVAL_MS and whenever the tab regains focus, and compare
 * the referenced hash to the one this tab was loaded with. A mismatch
 * means a newer build is live, so reload.
 *
 * Skips the reload if the user is actively typing (a focused input/
 * textarea/contenteditable) at the moment a new build is detected, to
 * avoid discarding in-progress work mid-keystroke - it just tries again
 * on the next check instead of forcing it through.
 *
 * No-ops entirely outside a real production build (e.g. `npm start`'s dev
 * server doesn't emit a content-hashed main.js), so this never interferes
 * with local development.
 */

const CHECK_INTERVAL_MS = 60_000;
const MAIN_JS_PATTERN = /\/static\/js\/main\.[a-f0-9]+\.js/;

function extractMainJsPath(html) {
  const match = html.match(MAIN_JS_PATTERN);
  return match ? match[0] : null;
}

function isUserTyping() {
  const el = document.activeElement;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
}

export function startAutoUpdateWatcher() {
  const currentMainJs = extractMainJsPath(document.documentElement.outerHTML);
  if (!currentMainJs) return () => {}; // not a production build - nothing to watch

  let checking = false;
  const checkForUpdate = async () => {
    if (checking || document.visibilityState === 'hidden') return;
    checking = true;
    try {
      const res = await fetch('/', { cache: 'no-store' });
      const html = await res.text();
      const latestMainJs = extractMainJsPath(html);
      if (latestMainJs && latestMainJs !== currentMainJs && !isUserTyping()) {
        window.location.reload();
      }
    } catch {
      // offline or a transient network hiccup - just try again next tick
    } finally {
      checking = false;
    }
  };

  const intervalId = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') checkForUpdate();
  };
  document.addEventListener('visibilitychange', onVisibilityChange);

  return () => {
    clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibilityChange);
  };
}
